import { useState, memo } from 'react';
import {
  Autocomplete,
  CircularProgress,
  createFilterOptions,
  FormHelperText,
  IconButton,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { EKeyAction } from '../../data/Constants';

import type { IAutoCompleteSearchBarProps, IOption } from '../../../types';

/**
 * This method returns the search options label to show it  in the dropdown
 * ex: code:S795001_FUS0500,name:Operation Manuals ----> S795001_FUS0500(Operation Manuals)
 * @param {IOption| string }option the current option
 * @returns {string} return the string
 */
function getSearchOptionLabel(option: IOption | string): string {
  if (option?.code) {
    return `${option?.code} (${option.name})`;
  }
  return option;
}

/**
 * Returns the Search Bar component to search and add the option
 * @param {IAutoCompleteSearchBarProps} props parent component props
 * @returns {React.ReactElement} returns the autocomplete search bar component
 */
const AutoCompleteSearchComponent = (props: IAutoCompleteSearchBarProps): React.ReactElement => {
  const {
    label,
    options,
    initialValue,
    maxLength,
    showOptions,
    error,
    warning,
    loading,
    onValueSelect,
    handleSearch,
    validate,
    setShowOptions,
  } = props;

  const [searchValue, setSearchValue] = useState<string>(initialValue);

  /**
   * This method triggers when click on searchIcon or press the Enter key to call the api
   * @param {HTMLInputElement} e auto complete input event
   * @returns {void}
   */
  const handleChangeInput = (e: { target: HTMLInputElement; code: string }): void => {
    const newValue = e.target.value.trim();
    setShowOptions(true);

    //Call the  read api when user press the 'Enter' Key to fetch the search options
    if (e?.code === EKeyAction.Enter) {
      handleSearch(searchValue);
    }

    //validate and show the error message to the user
    validate(newValue);
    //set the value to the state
    setSearchValue(newValue || '');
  };

  /**
   * This method triggers when user select the  options from the dropdown
   * @param {string} value selected options
   * @returns {void}
   */
  const handleChangeOptions = (value: string | null): void => {
    const trimmedValue = value ? value.trim() : '';
    //set the search value to the state
    setSearchValue(trimmedValue);

    //call this method to update the current selected features to the parent component state
    onValueSelect(trimmedValue);
  };

  /**
   * This method triggers when click on the clear icon in the search text box
   * @param {string} _value current user selected value
   * @param {string} reason gives the user action ex:(clear, reset)
   * @returns {void}
   */
  const resetSearchValue = (_value: string, reason: string): void => {
    //Clear the search value from the text box
    if (reason === 'clear') {
      setSearchValue('');
    }
  };

  return (
    <>
      {warning && (
        <FormHelperText sx={{ mt: 2 }} error>
          {warning}
        </FormHelperText>
      )}
      <div className="auto-complete-searchbar">
        <Autocomplete
          id="auto-complete-searchbar"
          onBlur={() => setShowOptions(false)}
          freeSolo={true}
          data-testid="auto-complete-searchbar"
          value={searchValue ?? ''}
          open={showOptions}
          options={options ?? []}
          filterOptions={createFilterOptions({ trim: true })} // Create a custom filter that trims whitespace
          getOptionLabel={(option) => getSearchOptionLabel(option)}
          onChange={(_, newValue) => handleChangeOptions(newValue)}
          onInputChange={(_e, value, reason) => resetSearchValue(value, reason)}
          classes={{
            root: 'auto-complete-search-input',
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              error={!!error}
              onKeyUp={(e) => handleChangeInput(e)}
              slotProps={{
                ...params.inputProps,
                htmlInput: { ...params.inputProps, maxLength: maxLength + 1 },
              }}
            />
          )}
        />
        <IconButton
          onClick={() => handleSearch(searchValue)}
          className="auto-complete-search-button"
          disabled={loading}
        >
          {loading && <CircularProgress size={24} />}
          {!loading && <SearchIcon className="auto-complete-search-icon" />}
        </IconButton>
      </div>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </>
  );
};

export default memo(AutoCompleteSearchComponent);
